import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Switch,
  TextField, Tooltip, Typography, FormControlLabel,
} from '@mui/material';
import PageContainer from '../components/PageContainer';
import { ListCardField } from '../components/ListCardField';
import { useIsXs } from '../hooks/useMobileLayout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import {
  listUsers, listRoles, createUser, updateUser, deleteUser, addUserMembership, removeUserMembership,
  listSindicatos, listTenants,
} from '../api/services';
import type { AdminUser, Role, Sindicato } from '../../types/domain';
import { useAppDispatch, useAppSelector } from '../store/store';
import { showMessage } from '../store/uiSlice';

interface FormState {
  userId?: number;
  dni: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  tenantId: number | null;
  roleId: number | '';
}

const blank: FormState = { dni: '', email: '', firstName: '', lastName: '', password: '', isActive: true, isSuperAdmin: false, tenantId: null, roleId: '' };

export default function AdminUsers() {
  const isXs = useIsXs();
  const dispatch = useAppDispatch();
  const me = useAppSelector((s) => s.auth.user);
  const isSuperAdmin = !!me?.isSuperAdmin;

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [camaras, setCamaras] = useState<Sindicato[]>([]);
  const [orgPick, setOrgPick] = useState<{ tenantId: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterSindicatoId, setFilterSindicatoId] = useState<number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank);
  const [busy, setBusy] = useState(false);
  const [membershipUser, setMembershipUser] = useState<AdminUser | null>(null);
  const [newMembership, setNewMembership] = useState<{ tenantId: number | null; roleId: number | '' }>({ tenantId: null, roleId: '' });

  const listUsersParams = useMemo(() => {
    if (!isSuperAdmin) return me?.tenantId != null ? { tenantId: me.tenantId } : {};
    if (filterSindicatoId == null) return {};
    const c = camaras.find((x) => x.sindicatoId === filterSindicatoId);
    return c ? { tenantId: c.tenantId } : {};
  }, [isSuperAdmin, me?.tenantId, filterSindicatoId, camaras]);

  const scopeTenantForCreate = useMemo(() => {
    if (!isSuperAdmin) return me?.tenantId ?? null;
    if (filterSindicatoId == null) return orgPick[0]?.tenantId ?? null;
    return camaras.find((x) => x.sindicatoId === filterSindicatoId)?.tenantId ?? null;
  }, [isSuperAdmin, me?.tenantId, filterSindicatoId, camaras, orgPick]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      if (isSuperAdmin) {
        const [sRes, tRes] = await Promise.all([
          listSindicatos({ includeInactive: true }),
          listTenants({ includeInactive: false }),
        ]);
        setCamaras(sRes.sindicatos);
        const m = new Map<number, string>();
        tRes.tenants.forEach((t) => m.set(t.tenantId, t.name));
        sRes.sindicatos.forEach((c) => {
          if (!m.has(c.tenantId)) m.set(c.tenantId, c.tenantName || `Organización ·${c.tenantId}`);
        });
        setOrgPick([...m.entries()].map(([tenantId, name]) => ({ tenantId, name })));
      } else {
        setCamaras([]);
        setOrgPick([]);
      }

      const [usersRes, rolesRes] = await Promise.all([
        listUsers(listUsersParams as { tenantId?: number }),
        listRoles(),
      ]);
      setUsers(usersRes.users);
      setRoles(rolesRes.roles);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error al cargar usuarios';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSindicatoId, isSuperAdmin, me?.tenantId]);

  function startCreate() {
    setForm({ ...blank, tenantId: scopeTenantForCreate });
    setOpen(true);
  }

  function startEdit(u: AdminUser) {
    setForm({
      userId: u.userId, dni: u.dni, email: u.email || '', firstName: u.firstName || '', lastName: u.lastName || '',
      password: '', isActive: u.isActive, isSuperAdmin: u.isSuperAdmin,
      tenantId: null, roleId: '',
    });
    setOpen(true);
  }

  async function handleSave() {
    setBusy(true);
    try {
      if (form.userId) {
        await updateUser(form.userId, {
          email: form.email || null,
          firstName: form.firstName || null,
          lastName: form.lastName || null,
          isActive: form.isActive,
          isSuperAdmin: form.isSuperAdmin,
          ...(form.password ? { password: form.password } : {}),
        });
        dispatch(showMessage({ message: 'Usuario actualizado', severity: 'success' }));
      } else {
        await createUser({
          dni: form.dni,
          email: form.email || undefined,
          firstName: form.firstName || undefined,
          lastName: form.lastName || undefined,
          password: form.password || undefined,
          isSuperAdmin: form.isSuperAdmin,
          tenantId: form.tenantId ?? undefined,
          roleId: form.roleId === '' ? undefined : Number(form.roleId),
        } as Parameters<typeof createUser>[0]);
        dispatch(showMessage({ message: 'Usuario creado', severity: 'success' }));
      }
      setOpen(false);
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'No se pudo guardar';
      dispatch(showMessage({ message: msg, severity: 'error' }));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(u: AdminUser) {
    if (!window.confirm(`¿Desactivar usuario ${u.firstName || u.dni}?`)) return;
    try {
      await deleteUser(u.userId);
      dispatch(showMessage({ message: 'Usuario desactivado', severity: 'success' }));
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'No se pudo desactivar';
      dispatch(showMessage({ message: msg, severity: 'error' }));
    }
  }

  async function addMembership() {
    if (!membershipUser || newMembership.roleId === '') return;
    try {
      await addUserMembership(membershipUser.userId, {
        tenantId: newMembership.tenantId ?? undefined,
        roleId: Number(newMembership.roleId),
      });
      dispatch(showMessage({ message: 'Asignación guardada (organización CAMARAS)', severity: 'success' }));
      setNewMembership({ tenantId: null, roleId: '' });
      const updated = await listUsers(listUsersParams as { tenantId?: number });
      setUsers(updated.users);
      const updatedUser = updated.users.find((u) => u.userId === membershipUser.userId) || null;
      setMembershipUser(updatedUser);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error al asignar';
      dispatch(showMessage({ message: msg, severity: 'error' }));
    }
  }

  async function removeMembership(membershipId: number) {
    if (!membershipUser) return;
    if (!window.confirm('¿Quitar esta asignación al usuario?')) return;
    try {
      await removeUserMembership(membershipUser.userId, membershipId);
      dispatch(showMessage({ message: 'Asignación quitada', severity: 'success' }));
      const updated = await listUsers(listUsersParams as { tenantId?: number });
      setUsers(updated.users);
      const updatedUser = updated.users.find((u) => u.userId === membershipUser.userId) || null;
      setMembershipUser(updatedUser);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error al quitar';
      dispatch(showMessage({ message: msg, severity: 'error' }));
    }
  }

  return (
    <PageContainer>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>Usuarios</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Usuarios vinculados a la organización de cada registro CAMARAS (membresías y roles).
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: 1, sm: 'auto' } }}>
          {isSuperAdmin && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              fullWidth={isXs}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Filtros
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate} fullWidth={isXs} sx={{ flexShrink: 0 }}>Nuevo usuario</Button>
        </Stack>
      </Stack>

      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="body">
        <DialogTitle sx={{ fontWeight: 800 }}>Filtros</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>CAMARAS (alcance)</InputLabel>
              <Select
                label="CAMARAS (alcance)"
                value={filterSindicatoId ?? ''}
                onChange={(e) => setFilterSindicatoId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value="">Todas</MenuItem>
                {camaras.map((c) => (
                  <MenuItem key={c.sindicatoId} value={c.sindicatoId}>
                    {c.name}{c.tenantName ? ` — ${c.tenantName}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setFiltersOpen(false)} fullWidth={isXs}>Listo</Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflow: 'hidden', p: { xs: 1.5, sm: 2 } }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : users.length ? (
          <Grid container spacing={2}>
            {users.map((u) => (
              <Grid key={u.userId} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{u.dni}</Typography>
                        <Typography variant="body2" color="text.secondary">{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</Typography>
                      </Box>
                      <Stack direction="row">
                        <Tooltip title="Asignaciones"><IconButton onClick={() => setMembershipUser(u)} size="small"><LinkIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton onClick={() => startEdit(u)} size="small"><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Desactivar"><IconButton onClick={() => handleDelete(u)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                    <Chip size="small" color={u.isActive ? 'success' : 'default'} label={u.isActive ? 'Activo' : 'Inactivo'} sx={{ alignSelf: 'flex-start' }} />
                    <ListCardField label="Email">
                      <Typography variant="body2">{u.email || '—'}</Typography>
                    </ListCardField>
                    <ListCardField label="Organizaciones / roles">
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {u.isSuperAdmin && <Chip size="small" color="warning" label="SuperAdmin" />}
                        {u.memberships.map((m) => (
                          <Chip key={m.membershipId} size="small" label={`${m.tenantName} · ${m.roleName}`} variant={m.isDefault ? 'filled' : 'outlined'} />
                        ))}
                        {!u.memberships.length && !u.isSuperAdmin && <Chip size="small" color="error" label="Sin asignación" />}
                      </Stack>
                    </ListCardField>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No hay usuarios para mostrar.</Typography>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="paper">
        <DialogTitle>{form.userId ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="DNI" value={form.dni} onChange={(e) => setForm((f) => ({ ...f, dni: e.target.value }))} required disabled={!!form.userId} size="small" fullWidth />
            <TextField label="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} type="email" size="small" fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Nombre" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} fullWidth size="small" />
              <TextField label="Apellido" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} fullWidth size="small" />
            </Stack>
            <TextField label={form.userId ? 'Nueva contraseña (opcional)' : 'Contraseña'} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} type="password" size="small" fullWidth placeholder={form.userId ? '' : '123456'} />
            {!form.userId && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {isSuperAdmin && (
                  <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel>Organización inicial</InputLabel>
                    <Select label="Organización inicial" value={form.tenantId ?? ''} onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value === '' ? null : Number(e.target.value) }))}>
                      <MenuItem value="">Sin asignación inicial</MenuItem>
                      {orgPick.map((o) => <MenuItem key={o.tenantId} value={o.tenantId}>{o.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel>Rol inicial</InputLabel>
                  <Select label="Rol inicial" value={form.roleId} onChange={(e) => setForm((f) => ({ ...f, roleId: e.target.value === '' ? '' : Number(e.target.value) }))}>
                    <MenuItem value="">Sin rol inicial</MenuItem>
                    {roles.filter((r) => r.roleName !== 'SuperAdmin').map((r) => <MenuItem key={r.roleId} value={r.roleId}>{r.roleName}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
              <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />} label="Activo" />
              {isSuperAdmin && <FormControlLabel control={<Switch checked={form.isSuperAdmin} onChange={(e) => setForm((f) => ({ ...f, isSuperAdmin: e.target.checked }))} />} label="SuperAdmin" />}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpen(false)} fullWidth={isXs}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={busy} fullWidth={isXs}>{busy ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!membershipUser} onClose={() => setMembershipUser(null)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="paper">
        <DialogTitle>Asignaciones de {membershipUser?.firstName || membershipUser?.dni}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Membresías actuales</Typography>
              {(membershipUser?.memberships || []).length ? (
                <Stack spacing={1}>
                  {membershipUser?.memberships.map((m) => (
                    <Stack key={m.membershipId} direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1}>
                      <Box>
                        <Typography variant="body2">{m.tenantName}</Typography>
                        <Typography variant="caption" color="text.secondary">{m.roleName}{m.isDefault ? ' · default' : ''}</Typography>
                      </Box>
                      <IconButton color="error" onClick={() => removeMembership(m.membershipId)}><LinkOffIcon /></IconButton>
                    </Stack>
                  ))}
                </Stack>
              ) : <Typography variant="body2" color="text.secondary">Sin asignaciones</Typography>}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Agregar membresía (organización CAMARAS)</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
                {isSuperAdmin && (
                  <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel>Organización</InputLabel>
                    <Select label="Organización" value={newMembership.tenantId ?? ''} onChange={(e) => setNewMembership((m) => ({ ...m, tenantId: e.target.value === '' ? null : Number(e.target.value) }))}>
                      <MenuItem value="">— elegí una —</MenuItem>
                      {orgPick.map((o) => <MenuItem key={o.tenantId} value={o.tenantId}>{o.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel>Rol</InputLabel>
                  <Select label="Rol" value={newMembership.roleId} onChange={(e) => setNewMembership((m) => ({ ...m, roleId: e.target.value === '' ? '' : Number(e.target.value) }))}>
                    <MenuItem value="">— elegí uno —</MenuItem>
                    {roles.filter((r) => r.roleName !== 'SuperAdmin').map((r) => <MenuItem key={r.roleId} value={r.roleId}>{r.roleName}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={addMembership} disabled={newMembership.roleId === ''} fullWidth={isXs}>Agregar</Button>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMembershipUser(null)} fullWidth={isXs}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
